import PropTypes from 'prop-types';
import React, { Component } from 'react';
import GeckoPermissions from 'services/gecko-permissions';
import GeckoRequest from 'services/gecko-request';
import GeckoPagination from 'services/gecko-pagination';
import { GeckoToastService, GECKO_CONSTS, GeckoCardTable } from 'ui';

class Teams extends Component {
  teamCols = [
    {
      key: 'status',
      align: 'left',
      type: GECKO_CONSTS.TABLE_CELL_TYPE_STATUS,
      styles: [
        {
          name: 'Available',
          type: GECKO_CONSTS.STATUS_TYPE_SUCCESS,
          key: 'Available',
        },
        {
          name: 'Unavailable',
          type: GECKO_CONSTS.STATUS_TYPE_WARNING,
          key: 'Unavailable',
        },
        {
          name: 'Offline',
          type: GECKO_CONSTS.STATUS_TYPE_DEFAULT,
          key: 'Offline',
        },
      ],
    },
    {
      key: 'name',
      type: GECKO_CONSTS.TABLE_CELL_TYPE_NAME,
    },
    {
      key: 'memberStatus',
      align: 'left',
      render: (val) => this.getTeamIcon(val),
      width: '10%',
    },
  ];

  getTeamIcon(memberDets) {
    if (memberDets.isMember) {
      return (
        <span
          className="member gecko-btn btn btn-outline-primary icon-only"
          onClick={() => this.toggleTeamMembership(memberDets)}
        >
          <i className="far fa-check"></i>
          <i className="far fa-times"></i>
        </span>
      );
    } else {
      return (
        <span
          className="non-member gecko-btn btn btn-outline-primary icon-only"
          onClick={() => this.toggleTeamMembership(memberDets)}
        >
          <i className="far fa-plus"></i>
        </span>
      );
    }
  }

  toggleTeamMembership(memberDets) {
    if (GeckoPermissions.memberOf('agent')) {
      return;
    }
    if (memberDets.isMember) {
      GeckoRequest.delete(`/models/agent_teams/${memberDets.agent_team_id}`)
        .then(() => {
          new GeckoToastService().success(`Agent removed from team`);
        })
        .catch((err) => {
          new GeckoToastService().error(`Unable to remove from team - ${err}`);
        });
    } else {
      GeckoRequest.post(`/models/agent_teams/`, {
        agent_id: this.props.agent.id,
        team_id: memberDets.team_id,
      })
        .then(() => {
          new GeckoToastService().success(`Agent added to team`);
        })
        .catch((err) => {
          new GeckoToastService().error(`Unable to add team member - ${err}`);
        });
    }
  }

  getTeamRows() {
    const membershipIDs = this.props.agent_teams
      .filter(
        (item) =>
          item.agent_id === this.props.agent.id &&
          item.account_id === this.props.loggedInUser.account_id
      )
      .map((item) => item.team_id);
    return this.getMemberships(membershipIDs).concat(
      this.getNonMemberships(membershipIDs)
    );
  }

  getMemberships(membershipIDs) {
    return this.props.teams
      .filter((team) => membershipIDs.indexOf(team.id) > -1)
      .map((team) => {
        return {
          id: team.id,
          name: team.name,
          status: this.getTeamStatus(team),
          memberStatus: {
            isMember: true,
            agent_team_id: this.getAgentTeamID(team),
            team_id: team.id,
            team_name: team.name,
          },
        };
      })
      .sort((a, b) => this.compareTeams(a, b));
  }

  getNonMemberships(membershipIDs) {
    return this.props.teams
      .filter((team) => membershipIDs.indexOf(team.id) < 0)
      .map((team) => {
        return {
          id: team.id,
          name: team.name,
          status: this.getTeamStatus(team),
          memberStatus: {
            isMember: false,
            team_id: team.id,
            team_name: team.name,
          },
        };
      })
      .sort((a, b) => this.compareTeams(a, b));
  }

  getAgentCrossPlatformStatus(agent) {
    if (agent.presence === 'Online') {
      return agent.web_available || agent.mobile_available
        ? 'Available'
        : 'Unavailable';
    } else {
      return 'Offline';
    }
  }

  getTeamStatus(team) {
    const memberIDs = this.props.agent_teams
      .filter(
        (item) =>
          item.team_id === team.id &&
          item.account_id === this.props.loggedInUser.account_id
      )
      .map((item) => item.agent_id);
    const memberStatuses = this.props.agents
      .filter((agent) => memberIDs.indexOf(agent.id) > -1)
      .map((agent) => this.getAgentCrossPlatformStatus(agent));
    let teamStatus;
    if (memberStatuses.indexOf('Available') > -1) {
      teamStatus = 'Available';
    } else if (memberStatuses.indexOf('Unavailable') > -1) {
      teamStatus = 'Unavailable';
    } else {
      teamStatus = 'Offline';
    }
    return teamStatus;
  }

  getAgentTeamID(team) {
    return this.props.agent_teams
      .filter(
        (item) =>
          item.agent_id === this.props.agent.id &&
          item.team_id === team.id &&
          item.account_id === this.props.loggedInUser.account_id
      )
      .map((item) => item.id)[0];
  }

  compareTeams(a, b) {
    if (
      a.status === 'Available' &&
      (b.status === 'Unavailable' ||
        b.status === 'Offline' ||
        b.status === null)
    ) {
      return -1;
    }
    if (
      a.status === 'Unavailable' &&
      (b.status === 'Offline' || b.status === null)
    ) {
      return -1;
    }
    if (
      (a.status === 'Unavailable' ||
        a.status === 'Offline' ||
        a.status === null) &&
      b.status === 'Available'
    ) {
      return 1;
    }
    if (
      (a.status === 'Offline' || b.status === null) &&
      b.status === 'Unavailable'
    ) {
      return 1;
    } else {
      if (a.name.toUpperCase() < b.name.toUpperCase()) {
        return -1;
      }
      if (a.name.toUpperCase() > b.name.toUpperCase()) {
        return 1;
      }
      return 0;
    }
  }

  render() {
    const [rows, pagination] = GeckoPagination.paginate({
      page: this.props.params.page,
      per_page: 5,
    })(this.getTeamRows());
    return (
      <div className="Detail-teams">
        <GeckoCardTable
          name="Teams"
          link="#/teams"
          cols={this.teamCols}
          rows={rows}
          pagination={pagination}
          alert="You have not created any Teams yet."
        />
      </div>
    );
  }
}

Teams.defaultProps = {
  params: { page: 1 },
};

Teams.propTypes = {
  agent: PropTypes.object.isRequired,
  agents: PropTypes.array.isRequired,
  agent_teams: PropTypes.array.isRequired,
  teams: PropTypes.array.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  params: PropTypes.object,
};

export default Teams;
